import { useEffect, useState, Fragment } from "react";

// Solman status aging (Oct 2026): open tickets and how long each has been in
// its current status, from solman_ticket_status_hist (recorded after every
// daily fetch). Click a row to see that ticket's status timeline.
const authToken = () =>
  localStorage.getItem("auth_token") ||
  new URLSearchParams(window.location.search).get("token") || "";

async function api(url) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${authToken()}` } });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

const cell = { padding: "8px 10px", borderBottom: "1px solid #e2e8f0", fontSize: 13, verticalAlign: "top" };
const head = { ...cell, fontWeight: 700, color: "#334155", background: "#f8fafc", textAlign: "left", whiteSpace: "nowrap" };

export default function SolmanStatusAging() {
  const [rows, setRows] = useState([]);
  const [minDays, setMinDays] = useState(7);
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [hist, setHist] = useState({});

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr("");
    api(`/api/solman/status-aging?minDays=${minDays}`)
      .then(r => { if (alive) setRows(r.data || []); })
      .catch(e => { if (alive) setErr(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [minDays]);

  const toggle = async (id) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id);
    if (!hist[id]) {
      try { const r = await api(`/api/solman/status-history/${encodeURIComponent(id)}`); setHist(h => ({ ...h, [id]: r.data || [] })); }
      catch (e) { setHist(h => ({ ...h, [id]: [{ status: "Error: " + e.message }] })); }
    }
  };

  const statuses = [...new Set(rows.map(r => r.status))].sort();
  const shown = status ? rows.filter(r => r.status === status) : rows;

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <strong style={{ fontSize: 15 }}>Open tickets by time in current status</strong>
        <label style={{ fontSize: 13 }}>Min. days&nbsp;
          <select value={minDays} onChange={e => setMinDays(Number(e.target.value))}>
            {[0, 3, 7, 14, 30, 60].map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 13 }}>Status&nbsp;
          <select value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <span style={{ fontSize: 12, color: "#64748b" }}>{shown.length} ticket(s)</span>
      </div>
      <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 10px" }}>
        History starts on 23 Oct 2026. Days marked "≥" were already in that status when tracking began, so the real age is longer.
      </p>
      {err && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 8 }}>{err}</div>}
      {loading ? <div style={{ fontSize: 13 }}>Loading…</div> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={head}>Ticket</th><th style={head}>Description</th><th style={head}>Company</th>
              <th style={head}>Comp.</th><th style={head}>Status</th><th style={head}>Days</th><th style={head}>Processor</th>
            </tr></thead>
            <tbody>
              {shown.map(r => (
                <Fragment key={r.ticket_id}>
                  <tr onClick={() => toggle(r.ticket_id)} style={{ cursor: "pointer", background: open === r.ticket_id ? "#f1f5f9" : undefined }}>
                    <td style={cell}>{r.ticket_id}</td>
                    <td style={cell}>{r.description}</td>
                    <td style={cell}>{r.company}</td>
                    <td style={cell}>{r.sap_component}</td>
                    <td style={cell}>{r.status}</td>
                    <td style={{ ...cell, fontWeight: 700, color: r.days_in_status >= 30 ? "#b91c1c" : r.days_in_status >= 14 ? "#b45309" : "#0f172a" }}>
                      {r.since_is_lower_bound ? "≥ " : ""}{r.days_in_status}
                    </td>
                    <td style={cell}>{r.processor}</td>
                  </tr>
                  {open === r.ticket_id && (
                    <tr><td colSpan={7} style={{ ...cell, background: "#f8fafc" }}>
                      {(hist[r.ticket_id] || []).length === 0 ? "Loading…" : (
                        <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                          {hist[r.ticket_id].map((h, i) => (
                            <li key={i}>{h.observed_at} — {h.prev_status ? `${h.prev_status} → ` : ""}<strong>{h.status}</strong>
                              {h.is_seed ? " (tracking start)" : ""}{h.processor ? ` · ${h.processor}` : ""}</li>
                          ))}
                        </ol>
                      )}
                    </td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
